import type { MediaAsset, SettingField as FieldSchema } from '@chicare/shared';
import { formatKobo, koboFromInput } from '@chicare/shared';

import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { FontPreview } from './FontPreview';

/**
 * One settings control, chosen by the field's declared type.
 *
 * This component is why adding a setting needs no frontend work at all: the
 * server declares a `SettingDefinition`, the schema arrives with the values,
 * and the right control renders here already validated. A new support-hours
 * field or a new threshold is one entry in one PHP file.
 */

export interface SettingFieldProps {
  field: FieldSchema;
  value: unknown;
  media?: MediaAsset;
  error?: string;
  dirty?: boolean;
  onChange: (value: unknown) => void;
  onPickImage?: (field: FieldSchema) => void;
}

export function SettingFieldControl({
  field,
  value,
  media,
  error,
  dirty,
  onChange,
  onPickImage,
}: SettingFieldProps) {
  const label = (
    <div className="mb-[5px] flex flex-wrap items-center gap-2">
      <label
        htmlFor={field.key}
        className="text-[11px] font-bold tracking-[0.01em] text-muted"
      >
        {field.label}
      </label>
      {field.requires_reason ? (
        // §9.11 made visible: an admin should know before they type that this
        // one will ask them why.
        <Badge tone="caution">needs a reason</Badge>
      ) : null}
      {field.public ? <Badge tone="brand">public</Badge> : null}
      {dirty ? <Badge tone="verified">changed</Badge> : null}
    </div>
  );

  return (
    <div className={cn('py-3.5', dirty && 'relative')}>
      {dirty ? (
        <span
          aria-hidden
          className="absolute -left-3 top-4 h-[calc(100%-2rem)] w-0.5 rounded-full bg-verified"
        />
      ) : null}

      {field.type !== 'boolean' ? label : null}

      <Control
        field={field}
        value={value}
        media={media}
        onChange={onChange}
        onPickImage={onPickImage}
        label={label}
      />

      {error ? (
        <p className="mt-1.5 text-[11px] font-medium text-danger">{error}</p>
      ) : field.help ? (
        <p className="mt-1.5 max-w-[62ch] text-[11px] leading-relaxed text-faint">{field.help}</p>
      ) : null}
    </div>
  );
}

function Control({
  field,
  value,
  media,
  onChange,
  onPickImage,
  label,
}: SettingFieldProps & { label: React.ReactNode }) {
  const asString = value === null || value === undefined ? '' : String(value);

  switch (field.type) {
    case 'boolean':
      return (
        <label className="flex cursor-pointer items-start gap-3">
          <button
            type="button"
            role="switch"
            id={field.key}
            aria-checked={Boolean(value)}
            onClick={() => onChange(!value)}
            className={cn(
              'relative mt-0.5 h-[23px] w-10 shrink-0 rounded-pill transition-colors',
              value ? 'bg-blue' : 'bg-line',
            )}
          >
            <span
              className={cn(
                'absolute top-[2.5px] h-[18px] w-[18px] rounded-full bg-white transition-all',
                value ? 'left-[19.5px]' : 'left-[2.5px]',
              )}
            />
          </button>
          <span className="min-w-0">{label}</span>
        </label>
      );

    case 'textarea':
    case 'richtext':
      return (
        <textarea
          id={field.key}
          value={asString}
          rows={field.type === 'richtext' ? 10 : 3}
          placeholder={field.placeholder ?? undefined}
          onChange={(event) => onChange(event.target.value)}
          className="w-full rounded-inp border border-line bg-white px-3 py-2.5 text-[13px] text-ink outline-none placeholder:text-faint focus:border-blue focus:ring-2 focus:ring-blue/15"
        />
      );

    case 'select': {
      // The one select whose value cannot be judged by reading it: a list of
      // font names asks somebody to choose between nine things they cannot
      // see. Everything else here is a word that means what it says.
      const isFont = field.key === 'brand.font_display' || field.key === 'brand.font_body';

      return (
        <div>
          <select
            id={field.key}
            value={asString}
            onChange={(event) => onChange(event.target.value)}
            className="min-h-[42px] w-full rounded-inp border border-line bg-white px-3 text-[13px] text-ink outline-none focus:border-blue focus:ring-2 focus:ring-blue/15"
          >
            {Object.entries(field.options ?? {}).map(([optionValue, optionLabel]) => (
              <option key={optionValue} value={optionValue}>
                {optionLabel}
              </option>
            ))}
          </select>

          {isFont ? (
            <FontPreview
              fontKey={asString}
              role={field.key === 'brand.font_display' ? 'display' : 'body'}
            />
          ) : null}
        </div>
      );
    }

    case 'money_kobo': {
      // Money crosses the wire as integer kobo (§9.10); the admin types naira.
      const kobo = typeof value === 'number' ? value : 0;

      return (
        <div>
          <Input
            id={field.key}
            adornment="₦"
            mono
            inputMode="decimal"
            defaultValue={(kobo / 100).toLocaleString('en-NG')}
            onBlur={(event) => onChange(koboFromInput(event.target.value))}
          />
          <p className="mt-1 font-mono text-[10.5px] text-faint tabular">
            {formatKobo(kobo)} · stored as {kobo.toLocaleString('en-NG')} kobo
          </p>
        </div>
      );
    }

    case 'months':
    case 'number':
    case 'percent':
      return (
        <div className="flex items-center gap-2.5">
          <Input
            id={field.key}
            type="number"
            mono
            value={asString}
            className="w-32"
            onChange={(event) =>
              onChange(event.target.value === '' ? null : Number(event.target.value))
            }
          />
          {field.unit ? <span className="text-xs text-muted">{field.unit}</span> : null}
        </div>
      );

    case 'image':
      return (
        <div className="flex items-center gap-3">
          <div className="grid h-16 w-16 shrink-0 place-items-center overflow-hidden rounded-lg border border-line bg-paper">
            {media ? (
              <img src={media.url} alt={media.alt ?? ''} className="h-full w-full object-contain" />
            ) : (
              <Icon name="camera" size={20} className="text-faint" />
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onPickImage?.(field)}
              className="touch-target rounded-btn border border-line bg-white px-3.5 text-xs font-bold text-muted hover:bg-paper"
            >
              {media ? 'Replace' : 'Upload'}
            </button>
            {media ? (
              <button
                type="button"
                onClick={() => onChange(null)}
                className="touch-target rounded-btn px-3.5 text-xs font-bold text-danger hover:bg-danger-tint"
              >
                Remove
              </button>
            ) : null}
          </div>
        </div>
      );

    case 'time':
      return (
        <Input
          id={field.key}
          type="time"
          mono
          value={asString}
          className="w-32"
          onChange={(event) => onChange(event.target.value)}
        />
      );

    default:
      return (
        <Input
          id={field.key}
          type={field.type === 'email' ? 'email' : field.type === 'url' ? 'url' : 'text'}
          inputMode={field.type === 'phone' ? 'tel' : undefined}
          value={asString}
          placeholder={field.placeholder ?? undefined}
          onChange={(event) => onChange(event.target.value)}
        />
      );
  }
}
